import React, { useState, useEffect } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const INTEREST_TAGS = [
  { id: "memoir",          label: "Memoir · the blind founder story" },
  { id: "scifi",           label: "Sci-fi · Federation-universe stories" },
  { id: "pattern-doctrine",label: "The Pattern Doctrine · philosophy" },
  { id: "ai-ethics",       label: "AI ethics · Truth Gate essays" },
  { id: "how-to",          label: "How-to · build-by-voice tutorials" },
  { id: "poetry",          label: "Poetry · short verse" },
];

export default function BooksPage() {
  const [name,      setName]      = useState("");
  const [email,     setEmail]     = useState("");
  const [interests, setInterests] = useState([]);
  const [note,      setNote]      = useState("");
  const [busy,      setBusy]      = useState(false);
  const [done,      setDone]      = useState(false);
  const [err,       setErr]       = useState("");
  const [count,     setCount]     = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/books/subscribers/count`)
      .then((r) => { if (alive) setCount(r.data?.count ?? null); })
      .catch(() => { /* swallow */ });
    return () => { alive = false; };
  }, []);

  function toggleInterest(id) {
    setInterests((cur) => cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  }

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setErr("Please share a valid email so I can reach you when something is ready.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/books/subscribe`, {
        email:     email.trim(),
        name:      name.trim() || null,
        interests,
        note:      note.trim() || null,
      });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || "Something went wrong — try again?");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <main className="mx-auto max-w-2xl px-5 py-16 text-slate-100" data-testid="books-page-done">
        <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/[0.05] p-8 text-center">
          <div className="mb-3 text-4xl">✓</div>
          <h1 className="mb-2 text-2xl font-semibold text-emerald-200">You&apos;re on the list.</h1>
          <p className="text-sm leading-relaxed text-slate-400">
            I&apos;ll write to <span className="text-slate-200">{email}</span> when the first
            piece is ready — not before. No newsletter, no auto-marketing,
            no funnel. Just the work, when it&apos;s done.
          </p>
          <p className="mt-4 text-sm text-slate-400">— Roger</p>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-2xl px-5 py-10 text-slate-100" data-testid="books-page">
      <header className="mb-8">
        <div className="mb-2 inline-block rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-300">
          Books & Stories · in progress
        </div>
        <h1 className="text-3xl font-semibold leading-tight sm:text-4xl">
          I write what I build.
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-slate-400">
          I&apos;m writing a few books and a stack of shorter pieces alongside
          the Federation work — memoir, sci-fi set in the NextXus universe,
          philosophy of the Pattern, how-to-build-things-blind. None of it is
          ready yet. If you want to know when the first piece lands, leave your
          email below. {count !== null && <span className="text-slate-300">{count} reader{count === 1 ? "" : "s"} already waiting.</span>}
        </p>
      </header>

      <form onSubmit={submit} className="rounded-2xl border border-slate-700/60 bg-slate-900/40 p-6" data-testid="books-form">
        <label className="mb-3 block">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">Email *</div>
          <input
            data-testid="books-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            placeholder="you@somewhere.com"
            required
            className="w-full rounded border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-400/60 focus:outline-none"
          />
        </label>

        <label className="mb-3 block">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">Name (optional)</div>
          <input
            data-testid="books-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="What should I call you?"
            className="w-full rounded border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-400/60 focus:outline-none"
          />
        </label>

        <fieldset className="mb-4">
          <legend className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">What pulls you in? (optional)</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {INTEREST_TAGS.map((t) => (
              <label
                key={t.id}
                className={`flex cursor-pointer items-center gap-2 rounded border px-3 py-2 text-xs transition-colors ${
                  interests.includes(t.id)
                    ? "border-amber-400/60 bg-amber-500/10 text-amber-200"
                    : "border-slate-700 text-slate-400 hover:bg-slate-800/60"
                }`}
              >
                <input
                  type="checkbox"
                  checked={interests.includes(t.id)}
                  onChange={() => toggleInterest(t.id)}
                  data-testid={`books-interest-${t.id}`}
                  className="h-3.5 w-3.5"
                />
                <span>{t.label}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <label className="mb-4 block">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">A note to me (optional)</div>
          <textarea
            data-testid="books-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Anything you want to tell me?"
            className="w-full rounded border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-400/60 focus:outline-none"
          />
        </label>

        {err && <div className="mb-3 rounded border border-rose-500/40 bg-rose-500/10 p-2 text-xs text-rose-300" data-testid="books-error">{err}</div>}

        <button
          data-testid="books-submit"
          type="submit"
          disabled={busy}
          className="rounded-lg bg-amber-500 px-5 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-50"
        >
          {busy ? "Saving…" : "Tell me when the first piece is ready"}
        </button>
        <p className="mt-3 text-[11px] text-slate-500">
          One email when something ships. No marketing. No funnel. You can reply
          to any message and reach me directly.
        </p>
      </form>

      <footer className="mt-8 border-t border-slate-800 pt-4 text-[11px] text-slate-500">
        — Roger Keyserling, Architect of the NextXus Federation
      </footer>
    </main>
  );
}
